import type { RouterOutputs } from "@apps/server/trpc/routers";
import { ActionIcon, Button, Flex, Group, Modal, Select, Text, Title, Tooltip } from "@mantine/core";
import { camelCaseToSnakeCase } from "@packages/lib";
import { IconFilter, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  MRT_GlobalFilterTextInput,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  useMantineReactTable,
} from "mantine-react-table";
import { useCallback, useMemo } from "react";
import { useBoolean } from "~/hooks/use-boolean";
import { errorNotification, successNotification } from "~/lib/notifications";
import { trpc } from "~/lib/trpc";

type Customer = RouterOutputs["customer"]["getCustomers"][number];

export const Route = createFileRoute("/_authenticated/customers/")({
  component: RouteComponent,
  loader: async ({ context: { queryClient, trpc } }) => {
    const [customers, fieldConfigs] = await Promise.all([
      queryClient.ensureQueryData(trpc.customer.getCustomers.queryOptions()),
      queryClient.ensureQueryData(
        trpc.crm.getFieldConfigsByTable.queryOptions({ table: "crm.customer" }),
      ),
    ]);
    return { customers, fieldConfigs };
  },
});

function RouteComponent() {
  const loaderData = Route.useLoaderData();
  const deleteModal = useBoolean();
  const navigate = useNavigate();
  const qu = useQueryClient();

  const customers = useQuery({
    ...trpc.customer.getCustomers.queryOptions(),
    initialData: loaderData.customers,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const deleteCustomers = useMutation(
    trpc.customer.delete.mutationOptions({
      onSuccess: (_, vars) => {
        table.resetRowSelection();
        qu.invalidateQueries(trpc.customer.getCustomers.queryOptions());
        successNotification(`Deleted ${vars.ids.length} customers`);
        deleteModal.close();
      },
      onError: (error) => {
        errorNotification(error.message);
      },
    }),
  );

  const getLabel = useCallback(
    (name: keyof Customer, fallback?: string) => {
      const field = loaderData.fieldConfigs[name];
      if (!field) return name;
      return field.config?.labelSv || field.config?.labelEn || fallback || name;
    },
    [loaderData.fieldConfigs],
  );

  const getCustomerTypeLabel = useCallback(
    (type: Customer["type"]) => {
      const option = loaderData.fieldConfigs.type?.config?.options?.find(op => op.id === type)
      if (!option) return type;
      return option.labelSv || option.labelSv || type;
    },
    [loaderData.fieldConfigs],
  );

  const columns = useMemo<MRT_ColumnDef<Customer>[]>(
    () => [
      {
        accessorFn: (row) => row?.name,
        id: "name",
        header: getLabel("name"),
        size: 250,
        filterVariant: "autocomplete",
      },
      {
        accessorFn: (row) => row.id,
        id: "id",
        header: getLabel("id", "Id"),
        size: 150,
      },
      {
        id: "email",
        accessorFn: (row) => row.email,
        enableClickToCopy: true,
        header: getLabel("email"),
        size: 300,
      },
      {
        id: "phone",
        accessorFn: (row) => row.phone,
        enableClickToCopy: true,
        header: getLabel("phone"),
        size: 200,
      },
      {
        id: "type",
        accessorFn: (row) => getCustomerTypeLabel(row.type),
        header: getLabel("type"),
        filterVariant: "select",
        size: 150,
      },
      {
        id: "identificationNumber",
        accessorFn: (row) => row.identificationNumber,
        header: getLabel("identificationNumber"),
        size: 250,
      },
      {
        accessorFn: (row) => {
          const dt = row.createdAt;
          return dt ? new Date(dt) : null;
        },
        id: "createdAt",
        header: getLabel("createdAt", "Created At"),
        filterVariant: "date-range",
        sortingFn: "datetime",
        enableColumnFilterModes: false, //keep this as only date-range filter with between inclusive filterFn
        Cell: ({ cell }) => {
          const dt = cell.getValue<Date>();
          if (!dt) return null;
          return dayjs(dt).format("YYYY-MM-DD hh:mm");
        }, //render Date as a string
      },
    ],
    [],
  );

  const table = useMantineReactTable({
    columns,
    data: customers.data, //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableFacetedValues: true,
    enableGrouping: true,
    // enablePinning: true,
    initialState: { showColumnFilters: false, showGlobalFilter: true },
    enablePagination: false,
    // enableDensityToggle: true,
    // createDisplayMode: "modal",

    // column
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    columnFilterDisplayMode: "popover",
    enableColumnPinning: true,
    enableColumnResizing: true,
    enableColumnVirtualization: true,

    // row
    enableRowNumbers: true,
    enableRowSelection: true,
    enableRowVirtualization: true,

    // header
    enableStickyHeader: true,

    // footer
    enableStickyFooter: true,
    enableTableFooter: true,

    mantineTableBodyRowProps: ({ row }) => ({
      onDoubleClick: () => {
        navigate({ to: "/customers/$customerId", params: { customerId: row.original.id.toString() } });
      },
      style: {
        cursor: "pointer",
      },
    }),

    mantinePaperProps: {
      // fixes double scrollbar
      mah: "100dvh",
    },
    mantineSearchTextInputProps: {
      placeholder: "Search Customers",
    },
    renderTopToolbar: ({ table }) => {
      const getSelectedIds = () => table.getSelectedRowModel().flatRows.map((row) => row.original.id);

      return (
        <>
          <Flex p="md" justify="space-between">
            <Flex gap="xs">
              <MRT_GlobalFilterTextInput table={table} />
              <Select variant="filled" leftSection={<IconFilter size={22} />} disabled placeholder="Filters" />
              <MRT_ShowHideColumnsButton table={table} />
              <MRT_ToggleFiltersButton table={table} />
            </Flex>
            <Flex style={{ gap: "8px" }}>
              <Tooltip label="Delete Customer" position="bottom" withArrow>
                <ActionIcon
                  color="red"
                  size="lg"
                  variant="light"
                  disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                  onClick={deleteModal.open}>
                  <IconTrash />
                </ActionIcon>
              </Tooltip>
            </Flex>
          </Flex>
          <Modal
            opened={deleteModal.state}
            onClose={deleteModal.close}
            title={<Title order={5}>Delete Customers</Title>}>
            <Text>Are you sure you want to delete {getSelectedIds().length} customers?</Text>
            <Group justify="flex-end" mt="md">
              <Button variant="neutral" onClick={deleteModal.close}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={deleteCustomers.isPending}
                onClick={() => deleteCustomers.mutate({ ids: getSelectedIds() })}>
                Delete
              </Button>
            </Group>
          </Modal>
        </>
      );
    },
  });

  return <MantineReactTable table={table} />;
}
